import { uuidToColor } from '#lib/helper/color.js';
import { isPromoCodeValidAt } from '#lib/helper/promoCode.js';
import {
	BookingStatus,
	DiscountType,
	PaymentMethod,
	PaymentStatus,
	ReconfirmationStatus,
	apiAppBookingBookingMarkasread,
	apiAppBookingBookingMarkasunread,
	apiAppBookingBookingSearch,
	apiAppBookingBookingSendsummary,
	apiAppBookingBookingUpdatestatus,
	hydrateBooking,
	type Booking,
	type BookingApiResponse,
	type BookingOffer,
	type BookingSearch,
	type BookingSession,
	type BookingSummary,
	type Customer,
	type CustomerPackageUsage,
	type CustomerWithBookings,
	type GuestPrice,
	type PromoCode,
	type Session,
	type SessionInstance
} from '#lib/types/generated/index.js';
import type { Icon } from '#lib/types/icons.js';
import { DateTime } from 'luxon';
import { derived, get, writable } from 'svelte/store';
import { adminStore } from './admin';
import { calendarDateRange, dateSelected } from './calendarDates';
import { addCustomerToStore } from './customer';
import { offerStore } from './offer';
import { optimisticSave } from './optimistic';
import { productStore } from './product';
import { getPromoCodeById } from './promoCode';
import { sessionStore } from './session';
import { sessionInstanceStore } from './session-instance';
import { now, today } from './time';

export const bookingStore = writable<Booking[]>([]);
export const bookingDaysStore = writable<Record<string, Booking[] | undefined>>({});
export const bookingHappeningDaysStore = derived([bookingDaysStore], ([$bookingDaysStore]) => {
	const out: Record<string, Booking[] | undefined> = {};
	Object.keys($bookingDaysStore).forEach((key) => {
		const b = $bookingDaysStore[key];
		if (b) {
			out[key] = b.filter((booking) => bookingIsHappening(booking));
		}
	});
	return out;
});

bookingStore.subscribe((bookings) => {
	const days: Record<string, Booking[] | undefined> = {};
	bookings.forEach((booking) => {
		const date = booking.date.toISODate();
		if (!date) return;
		if (!days[date]) days[date] = [];
		days[date].push(booking);
	});
	bookingDaysStore.set(days);
});

export const lastBookingIds = writable<Booking['id'][]>([]);

let loadBookingQueue: string[] = [];
let loadBookingTimeout: NodeJS.Timeout | null = null;
let loadBookingPromises: {
	resolve: (value: BookingSearch) => void;
	reject: (reason?: unknown) => void;
	ids: string[];
	callback: (booking: Booking) => void;
}[] = [];

export function loadBooking(
	id: string | string[],
	callback: (booking: Booking) => void = () => {}
): Promise<BookingSearch> {
	const idsToLoad = Array.isArray(id) ? id : [id];

	return new Promise((resolve, reject) => {
		loadBookingQueue.push(...idsToLoad);
		loadBookingPromises.push({ resolve, reject, ids: idsToLoad, callback });

		if (loadBookingTimeout) clearTimeout(loadBookingTimeout);

		loadBookingTimeout = setTimeout(() => {
			const currentQueue = [...new Set(loadBookingQueue)];
			const currentPromises = loadBookingPromises;

			loadBookingQueue = [];
			loadBookingPromises = [];
			loadBookingTimeout = null;

			apiAppBookingBookingSearch({
				id: currentQueue
			})
				.then((res) => {
					const items = res.items;
					addBookingToStore(items);

					currentPromises.forEach((p) => {
						const resDataSubset = items.filter((b) => p.ids.includes(b.id));
						resDataSubset.forEach((b) => {
							const converted = get(bookingStore).find(
								(storeB) => storeB.id === b.id
							);
							if (converted) {
								p.callback(converted);
							}
						});
						p.resolve(res);
					});
				})
				.catch((err) => {
					currentPromises.forEach((p) => p.reject(err));
				});
		}, 50);
	});
}

export function addBookingToStore(bookings: BookingApiResponse[] | Booking[]) {
	bookingStore.set([
		...get(bookingStore).filter((b) => !bookings.some((newBooking) => newBooking.id === b.id)),
		...bookings.map((booking) => {
			const b =
				'createdAt' in booking && typeof booking.createdAt !== 'string'
					? (booking as Booking)
					: hydrateBooking(booking as BookingApiResponse);
			if (b.customer) {
				addCustomerToStore(b.customer);
			}
			return b;
		})
	]);
}

export function clearBookingStore() {
	bookingStore.set([]);
	startDateLoaded = null;
	endDateLoaded = null;
}

function loadBookings(startDate: DateTime, endDate: DateTime, offset = 0) {
	const offsetLoading = 250;
	apiAppBookingBookingSearch({
		startDate: startDate.toISODate(),
		endDate: endDate.toISODate(),
		offset,
		limit: offsetLoading
	}).then((res) => {
		const items = res.items;
		addBookingToStore(items);
		if (items.length === offsetLoading) {
			loadBookings(startDate, endDate, offset + offsetLoading);
		}
	});
}

let startDateLoaded: DateTime | null = null;
let endDateLoaded: DateTime | null = null;

let bookingListenerLoaded = false;
export function setBookingListeners(): void {
	if (bookingListenerLoaded) return;
	bookingListenerLoaded = true;
	calendarDateRange.subscribe((v) => {
		if (
			!startDateLoaded ||
			!endDateLoaded ||
			(v.end <= endDateLoaded && v.start >= startDateLoaded)
		) {
			startDateLoaded = v.start;
			endDateLoaded = v.end;
			// We load the full period
			loadBookings(startDateLoaded, endDateLoaded);
		} else if (v.start < startDateLoaded) {
			// We load only the past period, the future is already loaded
			loadBookings(v.start, startDateLoaded);
			startDateLoaded = v.start;
		} else if (v.end > endDateLoaded) {
			// We load only the future period, the past is already loaded
			loadBookings(endDateLoaded, v.end);
			endDateLoaded = v.end;
		}
	});
}

export function updateBookingStore(booking: Booking) {
	if (booking.customer) {
		addCustomerToStore(booking.customer);
	}
	bookingStore.set([...get(bookingStore).filter((b) => b.id !== booking.id), booking]);
}

export function getBookingPax(booking: Booking): number | undefined {
	const res = Math.max(
		getOfferPax(booking) ?? 0,
		...booking.sessions.map((session) => session.pax)
	);
	return res === -Infinity ? undefined : res;
}

export function getOfferPax(booking: Booking): number | undefined {
	const offers = booking.offers;
	const res = Math.max(
		...offers.map((offer) => offer.prices.reduce((acc, price) => acc + price.quantity, 0))
	);
	return res === -Infinity ? undefined : res;
}

export function getOrderOfferPax(orderOffer: BookingOffer): number {
	return orderOffer.prices.reduce((acc, price) => acc + price.quantity, 0);
}

export function countBooking(customer: Customer | CustomerWithBookings): number {
	if (typeof customer.totalBookings === 'number' && customer.totalBookings > 0) {
		return customer.totalBookings;
	}
	if ('bookings' in customer) {
		return customer.bookings.filter((booking) => bookingIsValid(booking)).length;
	}
	return 0;
}

export function countNoShows(customer: Customer | CustomerWithBookings): number {
	if ('bookings' in customer) {
		return customer.bookings.filter(
			(booking) =>
				booking.status === BookingStatus.NoShow &&
				booking.date > get(now).minus({ days: 365 })
		).length;
	}
	return 0;
}

export function getSessionPaxList(sessions: BookingSession[]): number[] {
	return [...new Set([...sessions.map((session) => session.pax)])];
}

type Setting = {
	status: BookingStatus;
	icon: Icon;
	bgColor: string;
	beforeDate: boolean | undefined;
	hidden: boolean;
	allowSummaryEmail?: boolean;
	containerClasses: string;
	iconClasses: string;
};

export const bookingStatusSettings: Setting[] = [
	{
		status: BookingStatus.Intent,
		icon: 'internetEarth',
		bgColor: 'disabled',
		beforeDate: undefined,
		hidden: true,
		allowSummaryEmail: false,
		containerClasses:
			'bg-disabled dark:bg-disabled-dark text-disabled-on dark:text-disabled-on-dark',
		iconClasses: 'w-5 h-5'
	},
	{
		status: BookingStatus.ToBePaid,
		icon: 'creditCard',
		bgColor: 'disabled',
		beforeDate: undefined,
		hidden: true,
		allowSummaryEmail: false,
		containerClasses:
			'bg-disabled dark:bg-disabled-dark text-disabled-on dark:text-disabled-on-dark',
		iconClasses: 'w-5 h-5 p-0.5'
	},
	{
		status: BookingStatus.Booked,
		icon: 'loading',
		bgColor: 'disabled',
		beforeDate: undefined,
		hidden: true,
		allowSummaryEmail: false,
		containerClasses:
			'bg-disabled dark:bg-disabled-dark text-disabled-on dark:text-disabled-on-dark',
		iconClasses: 'w-5 h-5 p-0.5'
	},
	{
		status: BookingStatus.ToBeConfirmed,
		icon: 'magnify',
		bgColor: 'warning',
		beforeDate: false,
		hidden: true,
		allowSummaryEmail: true,
		containerClasses:
			'bg-warning dark:bg-warning-dark text-warning-on dark:text-warning-on-dark',
		iconClasses: 'w-5 h-5 p-0.5'
	},
	{
		status: BookingStatus.ToBeReconfirmed,
		icon: 'email',
		bgColor: 'warning',
		beforeDate: false,
		hidden: false,
		allowSummaryEmail: true,
		containerClasses:
			'bg-warning dark:bg-warning-dark text-warning-on dark:text-warning-on-dark',
		iconClasses: 'w-5 h-5 p-0.5'
	},
	{
		status: BookingStatus.Confirmed,
		icon: 'check',
		bgColor: 'success',
		beforeDate: undefined,
		hidden: false,
		allowSummaryEmail: true,
		containerClasses: 'text-success dark:text-success-dark',
		iconClasses: 'w-5 h-5 '
	},
	{
		status: BookingStatus.Left,
		icon: 'broom',
		bgColor: 'secondary',
		beforeDate: true,
		hidden: false,
		allowSummaryEmail: false,
		containerClasses: 'bg-end dark:bg-end-dark text-end-on dark:text-end-on-dark',
		iconClasses: 'w-5 h-5 p-0.5'
	},
	{
		status: BookingStatus.Canceled,
		icon: 'trash',
		bgColor: 'end',
		beforeDate: undefined,
		hidden: false,
		allowSummaryEmail: false,
		containerClasses: 'bg-end dark:bg-end-dark text-end-on dark:text-end-on-dark',
		iconClasses: 'w-5 h-5 p-1'
	},
	{
		status: BookingStatus.NoShow,
		icon: 'ghost',
		bgColor: 'error',
		beforeDate: true,
		hidden: false,
		allowSummaryEmail: false,
		containerClasses: 'bg-error dark:bg-error-dark text-error-on dark:text-error-on-dark',
		iconClasses: 'w-5 h-5 p-1'
	},
	{
		status: BookingStatus.Expired,
		icon: 'wind',
		bgColor: 'end',
		beforeDate: undefined,
		hidden: true,
		allowSummaryEmail: false,
		containerClasses: 'bg-end dark:bg-end-dark text-end-on dark:text-end-on-dark',
		iconClasses: 'w-5 h-5 p-0.5'
	}
];

export function getBookingSessionInstances(booking: Booking): SessionInstance[] {
	return booking.sessions
		.map((session) => session.sessionInstanceId)
		.map((sessionInstanceId) =>
			get(sessionInstanceStore).find(
				(sessionInstance) => sessionInstance.sessionInstanceId === sessionInstanceId
			)
		)
		.filter((sessionInstance) => sessionInstance !== undefined)
		.map((sessionInstance) => sessionInstance as SessionInstance);
}

export function bookingIsPaid(booking: Booking): boolean {
	const total = bookingGetTotalPrice(booking);

	return (
		Math.round(
			(total -
				booking.payments
					.filter((payment) => payment.status === PaymentStatus.Success)
					.reduce((acc, payment) => acc + payment.bookingAmount, 0)) *
				100
		) /
			100 ===
		0
	);
}

export function bookingGetTotalPrice(booking: Booking): number {
	return Math.max(
		0,
		bookingGetGrossTotalPrice(booking) -
			bookingGetPromoCodeDiscount(booking) -
			bookingGetCustomerPackageUsageCreditValue(booking.packageUsages, booking)
	);
}

function bookingGetGrossTotalPrice(booking: Booking): number {
	return (
		booking.offers.reduce(
			(acc, offerOrder) =>
				acc +
				// Offer base price
				(get(offerStore).find((offer) => offer.id === offerOrder.offerId)?.basePrice ?? 0) +
				// Guest prices
				offerOrder.prices
					.filter(
						(p) =>
							p.priceId ===
							get(offerStore)
								.find((o) => o.id === offerOrder.offerId)
								?.guestPrices.find(
									(gp) =>
										gp.id === p.priceId && (p.quantity > 0 || !gp.archivedAt)
								)?.id
					)
					.reduce((acc, price) => acc + price.quantity * price.unitPrice, 0),
			0
		) + getProductTotalPrice(booking)
	);
}

export function bookingGetPromoCodeDiscount(booking: Booking): number {
	if (booking.promoCodeId) {
		const promoCodeModel = getPromoCodeById(booking.promoCodeId);
		if (promoCodeModel) {
			let grossTotal = bookingGetGrossTotalPrice(booking);
			grossTotal -= bookingGetCustomerPackageUsageCreditValue(booking.packageUsages, booking);
			const pax = getBookingPax(booking) ?? 0;
			switch (promoCodeModel.discountType) {
				case DiscountType.Percentage:
					return (grossTotal * promoCodeModel.discountValue) / 100;
				case DiscountType.AmountPerPax:
					return promoCodeModel.discountValue * pax;
				case DiscountType.AmountPerBooking:
					return promoCodeModel.discountValue;
				default:
					return 0;
			}
		}
	}
	return 0;
}

function bookingGetCustomerPackageUsageCreditValue(
	packageUsages: CustomerPackageUsage[],
	booking?: Booking
): number {
	return packageUsages
		.filter((u) => u.offerId)
		.reduce((sum, u) => {
			if (u.guestPriceId) {
				const orderOffer = booking?.offers.find((o) => o.offerId === u.offerId);
				const price = orderOffer?.prices.find((p) => p.priceId === u.guestPriceId);
				// If we found a specific guest price but its unitPrice is missing, we fallback to 0
				if (price) {
					return sum + u.quantity * price.unitPrice;
				}
			}

			// Fallback: If no guest price was used OR the guest price was not found in the booking,
			// use the offer's base price ONCE (no multiplication by quantity).
			const offer = get(offerStore).find((o) => o.id === u.offerId);
			return sum + (offer?.basePrice ?? 0);
		}, 0);
}

export function getProductTotalPrice(booking: Booking): number {
	return Object.keys(booking.products).reduce(
		(acc, productId) =>
			acc +
			(get(productStore).find((p) => p.id === productId)?.price ?? 0) *
				booking.products[productId],
		0
	);
}

export function bookingGetRemainingAmount(booking: Booking): number {
	const total = bookingGetTotalPrice(booking);
	const remaining =
		Math.round(
			(total -
				booking.payments
					.filter((payment) => payment.status === PaymentStatus.Success)
					.reduce((acc, payment) => acc + payment.bookingAmount, 0)) *
				100
		) / 100;

	return remaining;
}

export function bookingHasSurveyResult(booking: Booking): boolean {
	return !!booking.surveyResult?.rating;
}

export function bookingIsHappening(booking: { status: BookingStatus }): boolean {
	return (
		booking.status !== BookingStatus.Canceled &&
		booking.status !== BookingStatus.NoShow &&
		booking.status !== BookingStatus.Expired
	);
}

export function bookingIsEnded(booking: { status: BookingStatus }): boolean {
	return (
		booking.status === BookingStatus.Canceled ||
		booking.status === BookingStatus.NoShow ||
		booking.status === BookingStatus.Expired ||
		booking.status === BookingStatus.Left
	);
}

export function bookingIsValid(booking: { status: BookingStatus }): boolean {
	return (
		booking.status !== BookingStatus.Expired &&
		booking.status !== BookingStatus.Intent &&
		booking.status !== BookingStatus.ToBePaid
	);
}

export function bookingIsConfirmed(booking: { status: BookingStatus }): boolean {
	return (
		booking.status !== BookingStatus.Canceled &&
		booking.status !== BookingStatus.NoShow &&
		booking.status !== BookingStatus.Expired &&
		booking.status !== BookingStatus.Intent &&
		booking.status !== BookingStatus.ToBePaid
	);
}

export function bookingIncoming(booking: BookingSummary): boolean {
	return (
		booking.status !== BookingStatus.Canceled &&
		booking.status !== BookingStatus.NoShow &&
		booking.status !== BookingStatus.Expired &&
		booking.status !== BookingStatus.Left &&
		booking.date >= get(today)
	);
}

export const getBookingColor = derived(
	[uuidToColor],
	([$uuidToColor]) =>
		(booking: Booking | undefined) => {
			if (!booking) return '';
			return $uuidToColor(booking.id);
		}
);

export function bookingHasOnlinePaymentToBeRefunded(booking: Booking): boolean {
	return booking.payments.some(
		(p) =>
			(p.method === PaymentMethod.Online || p.method === PaymentMethod.Giftcard) &&
			p.status === PaymentStatus.Success &&
			(p.bookingAmountRefunded || 0) < p.bookingAmount
	);
}

export function bookingStatusUpdate(booking: Booking, status: BookingStatus): Promise<Booking> {
	const updated = { ...booking, status };
	return optimisticSave(bookingStore, updated, () =>
		apiAppBookingBookingUpdatestatus(booking.id, { status })
	);
}

export function bookingConfirmedByCustomer(booking: Booking): void {
	booking.status = BookingStatus.Confirmed;
	booking.reconfirmationStatus = ReconfirmationStatus.Reconfirmed;
}

export function sendBookingSummaryEmail(booking: Booking): Promise<void> {
	return apiAppBookingBookingSendsummary(booking.id);
}

export function bookingMarkAsRead(booking: Booking): Promise<void> | void {
	if (get(adminStore)) return;
	if (!booking.unread) return;
	const updated = { ...booking, unread: false } as Booking;
	return optimisticSave(bookingStore, updated, () =>
		apiAppBookingBookingMarkasread(booking.id)
	).then(() => {});
}

export function bookingMarkAsUnread(booking: Booking): Promise<void> | void {
	if (!booking.unread) {
		const updated = { ...booking, unread: true } as Booking;
		return optimisticSave(bookingStore, updated, () =>
			apiAppBookingBookingMarkasunread(booking.id)
		).then(() => {});
	}
}

export function getBookingGuestPricePax(orderOffer: BookingOffer, guestPrice: GuestPrice): number {
	return orderOffer.prices.find((p) => p.priceId === guestPrice.id)?.quantity ?? 0;
}

export function bookingGetConferenceUrl(booking: Booking | undefined): string | null {
	return (
		booking?.sessions
			.map(
				(s) =>
					getBookingSessionInstances(booking).find(
						(si) => s.sessionInstanceId === si.sessionInstanceId && si.conferenceUrl
					)?.conferenceUrl
			)
			.find((u) => !!u) ?? null
	);
}

export function checkPromoCodeValidity(promo: PromoCode, booking: Booking): string | null {
	// Active / archived
	if (!promo.isActive || promo.archivedAt) {
		return 'promo_code.inactive';
	}

	// Usage limit (global)
	if (promo.usageLimit !== null) {
		const usageCount = get(bookingStore).filter((b) => b.promoCodeId === promo.id).length;
		if (usageCount > promo.usageLimit) {
			// If the booking already has this promo code, it does not count as a new usage
			if (booking.promoCodeId !== promo.id) {
				return 'promo_code.usage_limit_reached';
			}
		}
	}

	// Min amount
	if (promo.minAmount !== null) {
		if (bookingGetGrossTotalPrice(booking) < promo.minAmount) {
			return 'promo_code.min_amount_not_reached';
		}
	}

	// Limit per customer
	if (promo.limitPerCustomer !== null && booking.customer) {
		const customerBookings: BookingSummary[] =
			'bookings' in booking.customer
				? (booking.customer as CustomerWithBookings).bookings
				: [];
		let usageCount = customerBookings.filter((b) => b.promoCodeId === promo.id).length;

		// If we are re-validating the same booking, subtract it
		if (booking.promoCodeId === promo.id) {
			usageCount -= 1;
		}

		if (usageCount >= promo.limitPerCustomer) {
			return 'promo_code.limit_per_customer_reached';
		}
	}

	// New customers only
	if (promo.newCustomersOnly && booking.customer) {
		const hasPreviousBookings =
			booking.customer.totalBookings > 0 ||
			(booking.customer.firstBookingId !== null &&
				booking.customer.firstBookingId !== booking.id);

		if (hasPreviousBookings) {
			return 'promo_code.new_customers_only';
		}
	}

	// Data validity
	if (!isPromoCodeValidAt(promo, booking.date)) {
		return 'promo_code.error.date_not_valid';
	}

	// Scope (offers, sessions, activities, products)
	if (!promo.applyToAllBookings) {
		let applicable = false;

		// Offers
		if (promo.applyToOffers.length) {
			applicable = booking.offers.some((bo) =>
				promo.applyToOffers.includes(String(bo.offerId))
			);
		}

		// Sessions / activities
		if (!applicable && (promo.applyToSessions.length || promo.applyToActivities.length)) {
			let activitiesSessions: Session['id'][] = [];
			if (promo.applyToActivities.length) {
				activitiesSessions = get(sessionStore)
					.filter((s) => promo.applyToActivities.includes(String(s.parentId)))
					.map((s) => s.id);
			}
			applicable = booking.sessions.some((bs) =>
				promo.applyToSessions
					.concat(activitiesSessions.map(String))
					.includes(String(bs.sessionId))
			);
		}

		// Products
		if (!applicable && promo.applyToProducts.length) {
			applicable = Object.keys(booking.products).some((productId) =>
				promo.applyToProducts.includes(productId)
			);
		}

		if (!applicable) {
			return 'promo_code.not_applicable';
		}
	}

	return null;
}

export let countPastBooking = derived(
	[dateSelected],
	($dateSelected) =>
		(customer: Customer | CustomerWithBookings, dateOverrided: DateTime | undefined) => {
			if ('bookings' in customer) {
				return customer.bookings.filter(
					(booking) =>
						booking.date < (dateOverrided ? dateOverrided : $dateSelected) &&
						booking.status !== BookingStatus.Canceled &&
						booking.status !== BookingStatus.NoShow
				).length;
			}
			return customer.totalBookings > 0 ? 1 : 0;
		}
);
